<template>
<div>
    <Breadcrumbs main="Sales" submain="Collection" title="Sales Collection" />
    <div class="container-fluid">
        <div style="padding: 0px 12px; margin-bottom: 16px; display: flex; justify-content: end;">
            <b-button variant="primary" @click="goBack">
                <div style="display: flex; align-items: center; gap: 8px;">
                    <feather type="arrow-left" size="18px"></feather>
                    <h7>Back</h7>
                </div>
            </b-button>
        </div>

        <div class="row">
            <div class="col-md-12">
                <div class="card">
                    <div class="card-body">

                        <!-- ── Tab buttons ── -->
                        <div style="display: flex; gap: 8px; margin-bottom: 20px;">
                            <button :class="['btn', activeTab === 'collection' ? 'btn-primary' : 'btn-outline-secondary']" @click="switchTab('collection')">
                                Sales Collection
                            </button>
                            <button :class="['btn', activeTab === 'due_bill' ? 'btn-primary' : 'btn-outline-secondary']" @click="switchTab('due_bill')">
                                Due Bill
                            </button>
                            <button :class="['btn', activeTab === 'invoices' ? 'btn-primary' : 'btn-outline-secondary']" @click="switchTab('invoices')">
                                Collected Invoices
                            </button>
                        </div>

                        <!-- ── Filters & per-page ── -->
                        <b-row>
                            <b-col xl="6">
                                <div class="d-flex flex-wrap" style="gap: 10px;">
                                    <b-input-group class="datatable-btn" style="width: 340px;">
                                        <b-form-input v-model="filter" placeholder="Type to Search"></b-form-input>
                                        <b-input-group-append>
                                            <b-button :disabled="!filter" @click="filter = ''">Clear</b-button>
                                        </b-input-group-append>
                                    </b-input-group>
                                    <div v-if="start_date || end_date" style="display: flex; gap: 12px; align-items: center; color: white; background-color: #DC3545; padding: 2px 14px; max-width: fit-content; border-radius: 5px; cursor: pointer;" @click="clearFilter()">
                                        <feather type="x"></feather>
                                        <h7>Clear All</h7>
                                    </div>
                                </div>
                            </b-col>

                            <b-col xl="6">
                                <b-form-group label-cols="2" label="Per page" class="mb-0 datatable-select">
                                    <b-form-select v-model="perPage" :options="pageOptions"></b-form-select>
                                </b-form-group>
                            </b-col>
                        </b-row>

                        <!-- ── Table ── -->
                        <div v-if="!loading && !error && !loadingPage">
                            <div class="table-responsive datatable-vue" style="margin-top: 22px;">
                                <b-table v-if="activeTab === 'collection'" show-empty stacked="md" :items="activeItems" :fields="activeFields" :filter="filter" :per-page="perPage" @filtered="onFiltered">
                                    <template #cell(index)="data">
                                        {{ (currentPage - 1) * perPage + data.index + 1 }}
                                    </template>
                                    <template #cell(customer)="data">
                                        <span v-if="data.item?.customer">{{ data.item.customer.name }}</span>
                                    </template>
                                    <template #cell(action)="data">
                                        <span style="display: flex; gap: 12px;">
                                            <router-link :to="`/sales/sales-collection/${data.item.customer?.id}`">
                                                <feather type="search" size="20px" style="color: #568ae3; cursor: pointer;" v-b-tooltip.hover title="Overview"></feather>
                                            </router-link>
                                        </span>
                                    </template>
                                </b-table>

                                <!-- Due Bill tab -->
                                <b-table v-else-if="activeTab === 'due_bill'" show-empty stacked="md" responsive primary-key="id" :items="activeItems" :fields="activeFields" :filter="filter" :per-page="perPage" @filtered="onFiltered">
                                    <!-- Expand toggle -->
                                    <template #cell(show_details)="row">
                                        <div v-if="row.item.child_invoices && row.item.child_invoices.length">
                                            <feather :type="row.detailsShowing ? 'chevron-up' : 'chevron-down'" size="18" style="cursor: pointer" @click="row.toggleDetails" />
                                        </div>
                                    </template>

                                    <!-- Index -->
                                    <template #cell(index)="data">
                                        {{ (currentPage - 1) * perPage + data.index + 1 }}
                                    </template>

                                    <!-- Customer -->
                                    <template #cell(customer)="data">
                                        <div v-if="data.item?.customer">
                                            {{ data.item.customer.name }}
                                        </div>
                                    </template>

                                    <!-- Action -->
                                    <template #cell(action)="data">
                                        <span style="display: flex; gap: 12px;">
                                            <router-link :to="`/sales/sales-collection/${data.item.customer?.id}`">
                                                <feather type="search" size="20px" style="color: #568ae3; cursor: pointer;" title="Overview" />
                                            </router-link>
                                        </span>
                                    </template>

                                    <!-- Row details — child invoices -->
                                    <template #row-details="row">
                                        <div class="p-3 bg-light border rounded">
                                            <h6 class="mb-3">Child Invoices</h6>
                                            <b-table small responsive :items="row.item.child_invoices" :fields="childFields">
                                                <template #cell(index)="data">
                                                    {{ data.index + 1 }}
                                                </template>
                                                <template #cell(invoice_id)="data">
                                                    <router-link :to="`/sales/invoice/${data.item.invoice_id}`" class="link-style">
                                                        {{ data.item.invoice_id }}
                                                    </router-link>
                                                </template>
                                                <template #cell(challan_no)="data">
                                                    <!-- Need Id -->
                                                    <router-link :to="`/sales/challan-details/${data.item.challan_no}`" class="link-style">
                                                        {{ data.item.challan_no }}
                                                    </router-link>
                                                </template>
                                            </b-table>
                                        </div>
                                    </template>

                                </b-table>

                                <!-- Collected Invoices tab -->
                                <b-table v-else show-empty stacked="md" responsive :items="activeItems" :fields="activeFields" :filter="filter" :per-page="perPage" @filtered="onFiltered">
                                    <template #cell(index)="data">
                                        {{ (currentPage - 1) * perPage + data.index + 1 }}
                                    </template>

                                    <template #cell(invoice_id)="data">
                                        <span>
                                            <router-link :to="`/finance-and-accounts/finance/invoice/${data.item.invoice_id}`" class="link-style">
                                                {{ data.item.invoice_id }}
                                            </router-link>
                                        </span>
                                    </template>

                                    <template #cell(created_by)="data">
                                        <span>
                                            {{data.item.created_by.full_name }}
                                        </span>
                                    </template>

                                    <template #cell(project)="data">
                                        <div style="max-width: 300px;">
                                            <router-link :to="`/project/details/${data.item.project?.id}`" class="link-style">
                                                {{data.item.project?.title }}
                                            </router-link>
                                        </div>
                                    </template>

                                    <template #cell(subtotal)="data">
                                        <div class="d-flex flex-column" style="width: 170px;">
                                            <span class="font-weight-bold">Total: {{ data.item.subtotal }} {{ data.item.currency }}</span>
                                            <span> <span class="text-success font-weight-bold">Paid:</span> {{ data.item.paid_amount }} {{ data.item.currency }}</span>
                                            <span> <span class="text-danger font-weight-bold">Due:</span> {{ data.item.due_amount }} {{ data.item.currency }}</span>
                                        </div>
                                    </template>

                                    <template #cell(status)="row">
                                        <!-- @click="getStatus(row.value, row.item.id)" v-b-modal.modal-prevent-closing -->
                                        <span v-if="row.value == 'PARTIALLY-PAID'">
                                            <b-badge pill variant="success">{{ row.value }}</b-badge>
                                        </span>
                                        <span v-if="row.value == 'FULLY-PAID'">
                                            <b-badge pill variant="primary">{{ row.value }}</b-badge>
                                        </span>
                                        <span v-if="row.value == 'DUE'">
                                            <b-badge pill variant="danger">{{ row.value }}</b-badge>
                                        </span>
                                    </template>

                                </b-table>

                            </div>
                        </div>

                        <div v-else>
                            <div class="loader-box">
                                <div class="loader-10"></div>
                            </div>
                        </div>

                        <!-- ── Pagination ── -->
                        <b-col class="my-1 p-0">
                            <b-row class="mt-3">
                                <b-col xl="12" class="d-flex flex-wrap justify-content-start align-items-center" style="gap: 8px;">
                                    <span>
                                        <button class="pg-button" :disabled="currentPage === 1" @click="goToPage(1)">
                                            <b-icon icon="chevron-double-left"></b-icon>
                                        </button>
                                        <button class="pg-button ml-2" :disabled="currentPage === 1" @click="goToPage(currentPage - 1)">
                                            <b-icon icon="chevron-left"></b-icon>
                                        </button>
                                    </span>

                                    <span class="align-self-center">
                                        <span class="d-flex align-items-center" style="gap: 6px;">
                                            <span>Page</span>
                                            <b-form-input v-model="pageInput" style="box-shadow: none; max-width: 40px;" @input="handlePageInput" @blur="validatePage" @keydown.enter="validatePage" :disabled="totalPages <= 1" :max="totalPages"></b-form-input>
                                            <span>of {{ totalPages }}</span>
                                        </span>
                                    </span>

                                    <span>
                                        <button class="mr-2 pg-button" :disabled="currentPage === totalPages" @click="goToPage(currentPage + 1)">
                                            <b-icon icon="chevron-right"></b-icon>
                                        </button>
                                        <button class="pg-button" :disabled="currentPage === totalPages" @click="goToPage(totalPages)">
                                            <b-icon icon="chevron-double-right"></b-icon>
                                        </button>
                                    </span>
                                </b-col>
                            </b-row>
                        </b-col>

                    </div>
                </div>
            </div>
        </div>
    </div>
</div>
</template>

<script>
import {
    mapState
} from 'vuex';
import Datepicker from 'vuejs-datepicker';

export default {
    components: {
        Datepicker
    },

    data() {
        return {
            activeTab: 'collection', // 'collection' | 'due_bill'

            collectionFields: [{
                    key: 'index',
                    label: '#'
                },
                {
                    key: 'customer',
                    label: 'Customer',
                    sortable: true
                },
                {
                    key: 'total_amount',
                    label: 'Total Amount',
                    sortable: true
                },
                {
                    key: 'collection_amount',
                    label: 'Collection Amount',
                    sortable: true
                },
                {
                    key: 'due_amount',
                    label: 'Due Amount',
                    sortable: true
                },
                {
                    key: 'return_amount',
                    label: 'Return Amount',
                    sortable: true
                },
                {
                    key: 'action',
                    label: 'Action',
                    sortable: false
                },
            ],

            dueTablefields: [{
                    key: 'show_details',
                    label: ''
                },
                {
                    key: 'index',
                    label: '#'
                },
                {
                    key: 'customer',
                    label: 'Customer'
                },
                {
                    key: 'total',
                    label: 'Total Amount'
                },
                {
                    key: 'paid_amount',
                    label: 'Collection Amount'
                },
                {
                    key: 'due_amount',
                    label: 'Due Amount'
                },
                {
                    key: 'action',
                    label: 'Action'
                },
            ],

            invoiceFields: [{
                    key: 'index',
                    label: '#'
                },
                {
                    key: 'invoice_id',
                    label: 'Invoice ID',
                    sortable: true
                },
                {
                    key: 'created_by',
                    label: 'Raised By',
                    sortable: true
                },
                {
                    key: 'email',
                    label: 'Email',
                    sortable: true
                },
                {
                    key: 'project',
                    label: 'Project',
                    sortable: true
                },
                {
                    key: 'subtotal',
                    label: 'Amount',
                    sortable: true
                },
                {
                    key: 'invoice_date',
                    label: 'Invoice Date',
                    sortable: true
                },
                {
                    key: 'due_date',
                    label: 'Due Date',
                    sortable: true
                },
                {
                    key: 'status',
                    label: 'Status',
                    sortable: true
                },

            ],
            childFields: [{
                    key: 'index',
                    label: '#'
                },
                {
                    key: 'invoice_id',
                    label: 'Invoice ID'
                },
                {
                    key: 'challan_no',
                    label: 'Challan No'
                },
                {
                    key: 'total',
                    label: 'Total'
                },
                {
                    key: 'paid_amount',
                    label: 'Paid'
                },
                {
                    key: 'due_amount',
                    label: 'Due'
                },
                {
                    key: 'status',
                    label: 'Status'
                }
            ],

            filter: null,
            start_date: null,
            end_date: null,
            filterShow: false,

            currentPage: 1,
            perPage: 25,
            pageOptions: [25, 50, 100],
            pageInput: 1,

            loadingPage: false,
            initialLoad: true,
            pageInputDebounce: null,
        };
    },

    computed: {
        ...mapState({
            items: state => state.order.sales_collection,
            count: state => state.order.sales_count,
            due_items: state => state.order.sales_due_bill,
            due_count: state => state.order.sales_due_count,
            invoice_items: state => state.order.sales_invoices,
            invoice_count: state => state.order.sales_invoice_count,

            loading: state => state.order.submitLoading,
            error: state => state.order.submitError,
        }),

        activeItems() {
            if (this.activeTab === 'collection') return this.items;
            if (this.activeTab === 'due_bill') return this.due_items;
            return this.invoice_items;
        },
        activeFields() {
            if (this.activeTab === 'collection') return this.collectionFields;
            if (this.activeTab === 'due_bill') return this.dueTablefields;
            return this.invoiceFields;
        },
        activeCount() {
            if (this.activeTab === 'collection') return this.count;
            if (this.activeTab === 'due_bill') return this.due_count;
            return this.invoice_count;
        },
        totalPages() {
            return Math.max(1, Math.ceil(this.activeCount / this.perPage));
        },
    },

    watch: {
        currentPage(newVal) {
            if (this.pageInput != newVal) this.pageInput = newVal;
            if (!this.loadingPage && !this.initialLoad) {
                this.updateRouteQuery();
                this.fetchData();
            }
        },
        perPage() {
            if (!this.initialLoad) {
                this.currentPage = 1;
                this.pageInput = 1;
                this.updateRouteQuery();
                this.fetchData();
            }
        },
    },

    mounted() {
        const q = this.$route.query;

        this.activeTab = ['due_bill', 'invoices'].includes(q.tab) ? q.tab : 'collection';

        this.currentPage = parseInt(q.page) || 1;
        this.pageInput = this.currentPage;
        this.perPage = parseInt(q.perPage) || 25;

        if (q.start_date) this.start_date = q.start_date;
        if (q.end_date) this.end_date = q.end_date;
        if (this.start_date || this.end_date) this.filterShow = true;

        this.updateRouteQuery();
        this.fetchBoth(); // fetch both tabs on first load
    },

    methods: {
        // ── Tab switching ──────────────────────────────────────────────
        switchTab(tab) {
            if (this.activeTab === tab) return;
            this.activeTab = tab;
            this.currentPage = 1;
            this.pageInput = 1;
            this.updateRouteQuery();
            this.fetchData(); // fetch only the newly active tab
        },

        // ── Data fetching ──────────────────────────────────────────────
        fetchBoth() {
            this.$store.dispatch('order/fetchSalesCollection', {
                perPage: this.perPage,
                currentPage: this.currentPage,
            });
            this.$store.dispatch('order/fetchSalesDueBill', {
                perPage: this.perPage,
                currentPage: this.currentPage,
            });
            this.$store.dispatch('order/fetchSalesCollectedInvoices', {
                perPage: this.perPage,
                currentPage: this.currentPage,
            }).then(() => {
                this.initialLoad = false;
            });
        },

        fetchData() {
            if (this.initialLoad) return;
            const action = {
                collection: 'order/fetchSalesCollection',
                due_bill: 'order/fetchSalesDueBill',
                invoices: 'order/fetchSalesCollectedInvoices',
            } [this.activeTab];
            this.$store.dispatch(action, {
                perPage: this.perPage,
                currentPage: this.currentPage,
            });
        },

        // ── URL sync ───────────────────────────────────────────────────
        updateRouteQuery() {
            const query = {
                tab: this.activeTab,
                page: this.currentPage,
                perPage: this.perPage,
            };
            if (this.start_date) query.start_date = this.start_date;
            if (this.end_date) query.end_date = this.end_date;

            if (JSON.stringify(this.$route.query) !== JSON.stringify(query)) {
                this.$router.replace({
                    path: this.$route.path,
                    query
                }).catch(err => {
                    if (err.name !== 'NavigationDuplicated') throw err;
                });
            }
        },

        // ── Misc ───────────────────────────────────────────────────────
        goBack() {
            window.history.length > 1 ? this.$router.back() : this.$router.push('/dashboard');
        },
        clearFilter() {
            this.filterShow = false;
            this.start_date = null;
            this.end_date = null;
        },
        onFiltered(filteredItems) {
            this.currentPage = 1;
        },

        // ── Pagination ─────────────────────────────────────────────────
        goToPage(page) {
            if (page < 1 || page > this.totalPages) return;
            this.currentPage = page;
            this.pageInput = page;
        },
        handlePageInput() {
            if (this.pageInputDebounce) clearTimeout(this.pageInputDebounce);
            if (!this.pageInput || this.pageInput === '') return;
            this.pageInputDebounce = setTimeout(() => this.validatePage(), 800);
        },
        validatePage() {
            this.loadingPage = true;
            if (this.pageInputDebounce) {
                clearTimeout(this.pageInputDebounce);
                this.pageInputDebounce = null;
            }
            let page = parseInt(this.pageInput);
            if (!page || isNaN(page) || page < 1) page = 1;
            else if (page > this.totalPages) page = this.totalPages;
            this.pageInput = page;
            this.currentPage = page;
            this.loadingPage = false;
        },
    },
};
</script>
